import {Box, Stack} from '@chakra-ui/react';
import CustomTab from 'components/CustomTab';
import {find, get, last} from 'lodash';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useMatches, useNavigate} from 'react-router-dom';
import {paths} from 'routes/path';
import {useProfile} from 'hooks/datahook/auth';
import {useUserForexAccountList} from 'hooks/datahook/forex';
import {useAuthStore} from 'stores/authStore';

/**
 * IB portal layout. Branches on `data.preference.newUi`:
 *   newUi=true  → 3 tabs (Dashboard / IB Reports / Pending invitations)
 *                 + per-route child switches pick new vs old page bodies
 *   newUi=false → original 5 tabs (Overview / Sublevels / IB Reports /
 *                 Wired accounts IB reports / Pending invitations) +
 *                 original route bodies
 *
 * Default is `newUi=false` so admins can opt accounts in one by one.
 */
const IBLayout = () => {
    const {t} = useTranslation('app');
    const {user} = useAuthStore();
    const matches = useMatches();
    const matchPath = get(last(matches), 'pathname');
    const navigate = useNavigate();
    const {data: profile} = useProfile();
    const newUi = Boolean(get(profile, 'preference.newUi'));

    // Only fetch rewired-account list when we're rendering the old layout.
    // The new layout surfaces Rewired as an in-page section under IB Reports.
    const {data: rewiredData} = useUserForexAccountList({
        user: user.id,
        onlyRewired: true,
    });
    const rewiredCount = Array.isArray(rewiredData) ? rewiredData.length : 0;

    const oldTabs = [
        {
            label: t('overview'),
            value: 'overview',
            path: paths.ibOverview,
        },
        {
            label: t('referredClients'),
            value: 'sublevels',
            path: paths.ibSublevels,
        },
        {
            label: t('ibReports'),
            value: 'ibReports',
            path: paths.ibReports,
        },
        ...(rewiredCount
            ? [
                  {
                      label: t('wiredAccountsIbReports'),
                      value: 'wiredAccounts',
                      path: paths.wiredAccounts,
                  },
              ]
            : []),
        {
            label: t('pendingInvitations'),
            value: 'pendingInvitations',
            path: paths.pendingInvitations,
        },
    ];

    const newTabs = [
        {
            label: t('dashboard'),
            value: 'dashboard',
            path: paths.ibOverview,
        },
        {
            label: t('ibReports'),
            value: 'ibReports',
            path: paths.ibReports,
        },
        {
            label: t('pendingInvitations'),
            value: 'pendingInvitations',
            path: paths.pendingInvitations,
        },
    ];

    const tabs = newUi ? newTabs : oldTabs;
    // 2026-10-20 任务 K: 子路由(campaigns/marketing/education)不在 tab 列表里时
    // 不再回落到 tabs[0] —— 回落会让 Dashboard 恒为选中态, 而 Chakra Tabs 对已选中
    // tab 的点击不触发 onChange, 卡片页因此无法点回 Dashboard。value=undefined →
    // CustomTab findIndex 得 -1 → 全部 tab 不激活, 点任意 tab 正常跳转。
    const value = find(tabs, (o) => o.path === matchPath)?.value;

    return (
        <Stack spacing={4} width="100%">
            <CustomTab
                tabs={tabs}
                tabOnly
                value={value}
                onChange={(v) => {
                    navigate(find(tabs, (o) => o.value === v)?.path);
                }}
            />
            <Box width={'100%'}>
                <Outlet />
            </Box>
        </Stack>
    );
};

export default IBLayout;